import { FieldRenderProps } from "@progress/kendo-react-form";

export enum FormFieldTypesEnum {
  SingleResponse = 1,
  MultiResponse = 2,
  Dropdown = 3,
  Comment = 4,
  Toggle = 5,
  Number = 6,
  Name = 7,
  Email = 8,
  PhoneNumber = 9,
  Time = 10,
  Date = 11,
  PartialDate = 26,
  Slider = 12,
  Matrix = 13,
  Description = 14,
  WelcomeText = 15,
  ThankyouText = 16,
  Section = 17,
  HeaderHierarchyGroup = 18,
  HeaderDate = 19,
  HeaderText = 20,
  HeaderDropdown = 21,
  HeaderDeviceCameraCaptured = 22,

  IdentityFirstName = 51,
  IdentityLastName = 52,
  IdentityDateOfBirth = 53,
  IdentityEmail = 54,
  IdentityGender = 55,
  IdentityCountry = 56,
  IdentityAddress = 57,
  IdentityTown = 58,
  IdentityCity = 59,
  IdentityCounty = 60,
  IdentityPostcode = 61,
  IdentityPhoneHome = 62,
  IdentityPhoneMobile = 63,
  IdentityNationality = 64,
  IdentityLoginDetail = 65,
  IdentityLoginUsername = 66,
  IdentityLoginPassword = 67,
  IdentityLoginConfirmPassword = 68,
  IdentityLanguage = 69,
  IdentitySite = 70,
  IdentityPatientId = 71,
  IdentityAge = 72,
  IdentityAddressLookup = 73,

  CustomSingleResponse = 81,
  CustomMultiResponse = 82,
  CustomNumber = 83,
  CustomDate = 84,
  CustomText = 85,

  RegistrationStep = 91,
  RegistrationScreener = 92,

  ConsentHeader = 101,
  ConsentBody = 102,
  ConsentFooter = 103,
  ConsentDocument = 104,
  ReConsentBody = 105,

  ConsentCustomCheckbox = 121,
  ConsentCustomText = 122,
  ConsentCustomElectronicSignature = 123,
  ConsentCustomSingleResponse = 124,

  ScreenerField = 131,

  //declare enum type for dashboard
  DashboardTile = 151,
  HeadLinesText = 152,

  // other shared fields
  Hidden = 201,
}

export enum FormulaConditionOperatorEnum {
  "=" = 1,
  "!=" = 2,
  "<" = 3,
  ">" = 4,
  "<=" = 5,
  ">=" = 6,
}

export type Response = {
  id: number;
  text: string;
};

export type DependencyCondition = {
  key: string;
  parentId: number;
  responses?: number[]; // there will either be responses or 'responsevalue and compareOperator' but not both
  responseValue?: string;
  compareOperator?: string;
};

export type DependencyRule = {
  id: number;
  childAction: string;
  conditions: DependencyCondition[];
};

export enum ValidationTypesEnum {
  Required = 1,
  RegularExpression = 2,
  Range = 3,
  CountryCodeReguralExpression = 4,
  DependencyValidator = 5,
  InvalidResponses = 7,
  /** Compare current field value against another date or number field (admin type 8). */
  ComparisonValidation = 8,
  /** Validate sum of target fields plus current field against a threshold (admin type 9). */
  CalculatedValidation = 9,
}

/** Admin settings for comparison validation (validation type 8). */
export type ComparisonValidationSettings = {
  questionId: number;
  compareOperator: number;
};

/** Admin settings for calculated validation (validation type 9). */
export type CalculatedValidationSettings = {
  questionIds: number[];
  compareOperator: number;
  comparisonValue: number;
  /** When > 0, compare sum to this field's value instead of comparisonValue. */
  comparisonQuestionId?: number;
};

export type DynamicFieldValidatorSettings = {
  validatorName: string;
  validatorArgs?: string[];
  dependencyRuleId: number;
};
export type ValidationSettings = {
  regularExpression: string;
  regularExpressionValidationMessage?: string;
  rangeMinValue: string;
  rangeMaxValue: string;
  dependencyValidation?: DynamicFieldValidatorSettings;
  invalidResponses?: number[];
  comparisonValidation?: ComparisonValidationSettings;
  calculatedValidation?: CalculatedValidationSettings;
};

export type Validation = {
  validationType: number;
  validationMessage: string;
  settings: ValidationSettings;
  isActive?: boolean; // Controls whether validation should be applied for dependency rules.
};

export type Header = {
  id: number;
  text: string;
  fieldType: FormFieldTypesEnum;
  responses: Response[];
};

export type FormFieldItem = {
  id: number;
  fieldKeyName: string;
  stepId: number;
  uniqueStepId: number;
  title: string;
  disabled: boolean;
  hideOnLoad: boolean;
  text: string;
  tip: string;
  itemType: FormFieldTypesEnum; // fieldType
  displayOrder?: number;
  scoreCardId?: number;
  uniqueId: number; // cannot use Id as Ids will be duplicated across different types like sections, fields, so use uniqueId if we need some Id to uniquely identify an element in React
  responses: Response[];
  validations: Validation[];
  dependencyChildFieldIds: string[];
  dependencyRules: DependencyRule[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalProps?: any; // used for any additional details needed for the question
  screenerFailureFeedbackMessage: string;
};

export type FormFieldItemValues = {
  formFieldItems: FormFieldItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any;
  visitId: string;
  consentSubmittedWithoutPdf: boolean;
  /** Latitude from PortalIdentity; initialises the client coordinates ref on edit (not a form field). */
  lat?: string;
  /** Longitude from PortalIdentity; initialises the client coordinates ref on edit (not a form field). */
  lng?: string;
};

export interface FormFieldRenderProps extends FieldRenderProps {
  fieldItem: FormFieldItem | Header;
}

export type FormStepProps = {
  id: number;
  label: string;
  isValid: boolean | undefined;
  itemType: number;
  dependencyrules?: DependencyRule[]; //Camel-case is not supported
  screenerfailurefeedbackmessage: string; //Camel-case is not supported
};

export type FormFieldItemDetail = {
  id: number;
  stepId: number;
  itemType: number;
  value: string;
};

export type FormFieldItemValueDetail = {
  filedId: string;
  filedValue: string;
};

export type AddressLookupDetails = {
  summaryLine: string;
  address: string;
  town: string;
  county: string;
  postcode: string;
  /** WGS84 latitude returned by the Postcoder address lookup API. */
  latitude?: string;
  /** WGS84 longitude returned by the Postcoder address lookup API. */
  longitude?: string;
};

/**
 * Geographic coordinates captured from address lookup.
 * Held outside Kendo form state and sent on save via the registration API.
 */
export type AddressCoordinates = Readonly<{
  lat: string;
  lng: string;
}>;
